import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import WorkspacePremiumOutlinedIcon from '@mui/icons-material/WorkspacePremiumOutlined';
import { brandColors } from '@config/brandTokens';
import { useAuthStore } from '@store/authStore';
import {
  cancelSubscription,
  fetchMyInvoices,
  fetchMySubscription,
  fetchPlayerFamilyCatalog,
} from '@services/subscriptionService';
import { DEFAULT_SUBSCRIPTION_OFFERS, fetchSubscriptionOffers } from '@/content/subscriptionPricing';
import type { SubscriptionOffer } from '@/content/subscriptionPricing';
import {
  audienceLabelForRole,
  familyPlanSummary,
  inAppPlansPath,
  invoiceStripeStatus,
  isOccupiedSubscription,
  isPlayerParentRole,
  paymentGatewayPath,
  planDisplayName,
  PLAYER_FAMILY_CATALOG,
  subscriptionStatusLabel,
  type PlayerFamilyCatalog,
} from '@/content/subscriptionCatalog';
import { formatCatalogYearly } from '@/content/subscriptionPricing';
import {
  downloadHtml,
  formatInvoiceAmount,
  formatUtcDate,
  groupInvoicesByUtcMonth,
  invoiceHtml,
  monthInvoicesHtml,
} from '@/content/billingFormat';
import type { BillingInvoice, UserSubscription } from '@types/index';
import ChangePlanDialog from './ChangePlanDialog';
import PrebookDialog from './PrebookDialog';
import RaisePaymentIssueDialog from './RaisePaymentIssueDialog';

const quietCardSx = {
  p: { xs: 2, md: 2.5 },
  borderRadius: 2,
  bgcolor: brandColors.white,
  border: '1px solid #e2e8f0',
} as const;

const tableHeaderSx = {
  display: { xs: 'none', sm: 'grid' },
  gridTemplateColumns: '1.1fr 1.6fr 0.7fr 1fr 0.7fr',
  gap: 1,
  px: 1.25,
  pb: 0.75,
} as const;

const tableRowSx = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', sm: '1.1fr 1.6fr 0.7fr 1fr 0.7fr' },
  gap: 1,
  alignItems: 'center',
  px: 1.25,
  py: 1.15,
  border: '1px solid #e2e8f0',
  borderRadius: 1.5,
  bgcolor: brandColors.ice,
} as const;

function statusChipSx(status?: string | null) {
  if (status === 'active') return { bgcolor: '#ecfdf5', color: '#047857' };
  if (status === 'prebooked') return { bgcolor: '#fff7ed', color: '#c2410c' };
  if (status === 'beta') return { bgcolor: '#ecfdf5', color: '#047857' };
  if (status === 'cancelled') return { bgcolor: '#f8fafc', color: brandColors.slate };
  if (status === 'expired') return { bgcolor: '#f8fafc', color: brandColors.slate };
  return { bgcolor: brandColors.ice, color: brandColors.navy };
}

const GATEWAYS = [
  {
    id: 'stripe' as const,
    name: 'Stripe',
    detail: 'International cards',
  },
  {
    id: 'razorpay' as const,
    name: 'Razorpay',
    detail: 'India cards, UPI, and netbanking',
  },
];

const BillingInvoicesPanel: React.FC = () => {
  const navigate = useNavigate();
  const { user, setUser } = useAuthStore();
  const [subscription, setSubscription] = useState<UserSubscription | null>(user?.subscription || null);
  const [invoices, setInvoices] = useState<BillingInvoice[]>([]);
  const [catalog, setCatalog] = useState<PlayerFamilyCatalog>(PLAYER_FAMILY_CATALOG);
  const [offers, setOffers] = useState<SubscriptionOffer[]>(DEFAULT_SUBSCRIPTION_OFFERS);
  const [loading, setLoading] = useState(true);
  const [viewing, setViewing] = useState<BillingInvoice | null>(null);
  const [changeOpen, setChangeOpen] = useState(false);
  const [prebookOpen, setPrebookOpen] = useState(false);
  const [issueOpen, setIssueOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [actionError, setActionError] = useState('');

  const role = String(user?.role || '');
  const plansPath = inAppPlansPath(role);
  const occupied = isOccupiedSubscription(subscription?.status);
  const player = isPlayerParentRole(role);
  const playerOffer = offers.find((o) => o.id === 'players_parents') || offers[0];

  const applySubscription = (next: UserSubscription) => {
    setSubscription(next);
    if (user) setUser({ ...user, subscription: next });
  };

  const reloadInvoices = async () => {
    const list = await fetchMyInvoices();
    setInvoices(list);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [mine, list, familyCatalog, offerList] = await Promise.all([
        fetchMySubscription(),
        fetchMyInvoices(),
        fetchPlayerFamilyCatalog(),
        fetchSubscriptionOffers(),
      ]);
      if (cancelled) return;
      if (mine) applySubscription(mine);
      setInvoices(list);
      if (familyCatalog) setCatalog(familyCatalog);
      if (offerList.length) setOffers(offerList);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const monthGroups = useMemo(() => groupInvoicesByUtcMonth(invoices), [invoices]);

  const planName = subscription?.planConfig
    ? planDisplayName(subscription.planConfig)
    : occupied
      ? audienceLabelForRole(role)
      : 'None';
  const cycleStart = formatUtcDate(subscription?.startedAt || subscription?.planConfig?.startDate);
  const cycleEnd = formatUtcDate(subscription?.expiresAt);
  const startDate = formatUtcDate(subscription?.planConfig?.startDate || subscription?.startedAt);

  const handleCancel = async () => {
    setCancelling(true);
    setActionError('');
    try {
      const next = await cancelSubscription();
      applySubscription(next);
      await reloadInvoices();
      setCancelOpen(false);
    } catch (err: unknown) {
      const anyErr = err as { response?: { data?: { message?: string } }; message?: string };
      setActionError(anyErr.response?.data?.message || anyErr.message || 'Could not cancel');
    } finally {
      setCancelling(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Stack spacing={2}>
      <Box>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 800,
            color: brandColors.navy,
            fontFamily: '"Sora", Arial, sans-serif',
            mb: 0.5,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <ReceiptLongOutlinedIcon sx={{ fontSize: 26 }} />
          Billing & invoices
        </Typography>
        <Typography sx={{ color: brandColors.slate, fontSize: '0.9rem' }}>
          Yearly plan, card management, and receipts.
        </Typography>
      </Box>

      <Box sx={quietCardSx}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }} useFlexGap flexWrap="wrap">
          <WorkspacePremiumOutlinedIcon sx={{ fontSize: 20, color: brandColors.navy }} />
          <Typography sx={{ fontWeight: 800, color: brandColors.navy, fontFamily: '"Sora", Arial, sans-serif' }}>
            Current plan
          </Typography>
          <Chip
            size="small"
            label={subscriptionStatusLabel(subscription?.status)}
            sx={{ height: 22, fontWeight: 800, ...statusChipSx(subscription?.status) }}
          />
        </Stack>

        <Typography sx={{ fontWeight: 800, color: brandColors.navy, fontSize: '1.05rem', mb: 0.35 }}>
          {planName}
        </Typography>
        <Typography variant="body2" sx={{ color: brandColors.slate, lineHeight: 1.55 }}>
          Subscription cycle · yearly
          {occupied || subscription?.status === 'cancelled'
            ? ` · ${cycleStart} – ${cycleEnd}`
            : ''}
          {subscription?.expiresAt && subscription.status !== 'cancelled'
            ? ` · renews ${cycleEnd}`
            : ''}
          {subscription?.status === 'cancelled' && subscription.expiresAt
            ? ` · access until ${cycleEnd}`
            : ''}
          {subscription?.amountUsd != null
            ? ` · ${formatCatalogYearly(subscription.amountUsd, subscription.currency || catalog.currency)} / year`
            : ''}
        </Typography>
        {subscription?.status === 'prebooked' && (
          <Typography variant="body2" sx={{ color: '#c2410c', fontWeight: 700, mt: 0.75 }}>
            Pre-booked · {planName} · starts {startDate}
            {subscription.planConfig?.prebookIntake?.contact
              ? ` · reach you at ${subscription.planConfig.prebookIntake.contact}`
              : ''}
          </Typography>
        )}
        {subscription?.planConfig && occupied && (
          <Typography variant="body2" sx={{ color: brandColors.slate, mt: 0.5 }}>
            {familyPlanSummary(subscription.planConfig)}
          </Typography>
        )}
        {actionError && (
          <Typography variant="body2" sx={{ color: '#b91c1c', mt: 1 }}>
            {actionError}
          </Typography>
        )}

        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1.75 }}>
          {player && occupied && (
            <Button
              size="small"
              variant="contained"
              onClick={() => setChangeOpen(true)}
              sx={{
                textTransform: 'none',
                fontWeight: 800,
                bgcolor: brandColors.navy,
                '&:hover': { bgcolor: '#0a0f28' },
              }}
            >
              Upgrade / Downgrade
            </Button>
          )}
          {player && (
            <Button
              size="small"
              variant="outlined"
              onClick={() => setPrebookOpen(true)}
              disabled={subscription?.status === 'active' || subscription?.status === 'beta'}
              sx={{
                textTransform: 'none',
                fontWeight: 800,
                borderColor: brandColors.navy,
                color: brandColors.navy,
              }}
            >
              Pre-book
            </Button>
          )}
          {occupied && (
            <Button
              size="small"
              variant="outlined"
              onClick={() => setCancelOpen(true)}
              sx={{
                textTransform: 'none',
                fontWeight: 800,
                borderColor: '#cbd5e1',
                color: brandColors.slate,
              }}
            >
              Cancel subscription
            </Button>
          )}
          <Button
            size="small"
            onClick={() => navigate(plansPath)}
            sx={{ textTransform: 'none', fontWeight: 800, color: brandColors.navy }}
          >
            {occupied ? 'View plan' : 'View plans'}
          </Button>
        </Stack>
      </Box>

      <Box sx={quietCardSx}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }} useFlexGap flexWrap="wrap">
          <CreditCardOutlinedIcon sx={{ fontSize: 20, color: brandColors.navy }} />
          <Typography sx={{ fontWeight: 800, color: brandColors.navy, fontFamily: '"Sora", Arial, sans-serif' }}>
            Payment
          </Typography>
        </Stack>
        <Typography variant="body2" sx={{ color: brandColors.slate, mb: 1.5 }}>
          Manage cards on Stripe or Razorpay. Each gateway keeps its own card on file and purchase history.
        </Typography>
        <Stack spacing={1}>
          {GATEWAYS.map((gateway) => (
            <Box
              key={gateway.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                px: 1.5,
                py: 1.2,
                borderRadius: 1.5,
                border: '1px solid #e2e8f0',
                bgcolor: brandColors.ice,
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 800, color: brandColors.navy }}>{gateway.name}</Typography>
                <Typography variant="caption" sx={{ color: brandColors.slate }}>
                  {gateway.detail}
                </Typography>
              </Box>
              <Button
                size="small"
                variant="outlined"
                onClick={() => navigate(paymentGatewayPath(role, gateway.id))}
                sx={{
                  textTransform: 'none',
                  fontWeight: 800,
                  borderColor: brandColors.navy,
                  color: brandColors.navy,
                }}
              >
                Manage
              </Button>
            </Box>
          ))}
        </Stack>
        <Button
          size="small"
          onClick={() => setIssueOpen(true)}
          sx={{ mt: 1.5, textTransform: 'none', fontWeight: 800, color: brandColors.navy }}
        >
          Raise issue
        </Button>
      </Box>

      <Box sx={quietCardSx}>
        <Stack direction="row" spacing={1} alignItems="baseline" useFlexGap flexWrap="wrap" sx={{ mb: 0.25 }}>
          <Typography
            sx={{
              fontWeight: 800,
              color: brandColors.navy,
              fontFamily: '"Sora", Arial, sans-serif',
            }}
          >
            Invoices
          </Typography>
          <Typography variant="caption" sx={{ color: brandColors.slate, fontWeight: 700 }}>
            UTC
          </Typography>
        </Stack>

        {monthGroups.map((group) => (
          <Box key={group.key} sx={{ mt: 1.75 }}>
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
              <Typography sx={{ fontWeight: 800, color: brandColors.navy }}>{group.label}</Typography>
              <Button
                size="small"
                onClick={() =>
                  downloadHtml(
                    `encycgames-invoices-${group.key}.html`,
                    monthInvoicesHtml(group.label, group.invoices),
                  )
                }
                sx={{ textTransform: 'none', fontWeight: 800, color: brandColors.navy }}
              >
                Download
              </Button>
            </Stack>
            <Box sx={tableHeaderSx}>
              {['Date (UTC)', 'Description', 'Status', 'Amount', 'Invoice'].map((label) => (
                <Typography key={label} variant="caption" sx={{ color: brandColors.slate, fontWeight: 800 }}>
                  {label}
                </Typography>
              ))}
            </Box>
            {group.invoices.length === 0 ? (
              <Box sx={tableRowSx}>
                <Typography sx={{ color: '#94a3b8', fontWeight: 700, fontSize: '0.85rem' }}>—</Typography>
                <Typography sx={{ color: '#94a3b8', fontWeight: 700, fontSize: '0.85rem' }} />
                <Typography sx={{ color: '#94a3b8', fontWeight: 700, fontSize: '0.85rem' }} />
                <Typography sx={{ color: '#94a3b8', fontWeight: 700, fontSize: '0.85rem' }} />
                <Typography sx={{ color: '#94a3b8', fontWeight: 700, fontSize: '0.85rem' }} />
              </Box>
            ) : (
              <Stack spacing={1}>
                {group.invoices.map((invoice) => (
                  <Box key={invoice.id} sx={tableRowSx}>
                    <Typography sx={{ fontWeight: 700, color: brandColors.navy, fontSize: '0.88rem' }}>
                      {formatUtcDate(invoice.date)}
                    </Typography>
                    <Typography sx={{ fontWeight: 700, color: brandColors.navy, fontSize: '0.88rem' }}>
                      {invoice.description || invoice.planLabel || ''}
                    </Typography>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        color: brandColors.slate,
                        fontSize: '0.85rem',
                        textTransform: 'lowercase',
                      }}
                    >
                      {invoice.invoiceStatus || invoiceStripeStatus(invoice.status, invoice.kind)}
                    </Typography>
                    <Typography sx={{ fontWeight: 800, color: brandColors.navy, fontSize: '0.88rem' }}>
                      {formatInvoiceAmount(invoice.amountUsd, invoice.currency)}
                    </Typography>
                    <Button
                      size="small"
                      onClick={() => setViewing(invoice)}
                      sx={{ textTransform: 'none', fontWeight: 800, color: brandColors.navy, minWidth: 0, justifySelf: 'start' }}
                    >
                      View
                    </Button>
                  </Box>
                ))}
              </Stack>
            )}
          </Box>
        ))}
      </Box>

      <Dialog open={Boolean(viewing)} onClose={() => setViewing(null)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontFamily: '"Sora", Arial, sans-serif', fontWeight: 800, color: brandColors.navy }}>
          Invoice {viewing?.invoiceNumber}
        </DialogTitle>
        <DialogContent>
          {viewing && (
            <Stack spacing={1.1} sx={{ pt: 0.5 }}>
              <Typography variant="body2" sx={{ color: brandColors.slate }}>
                Date (UTC) · {formatUtcDate(viewing.date)}
              </Typography>
              <Typography variant="body2" sx={{ color: brandColors.slate }}>
                Description · {viewing.description || viewing.planLabel}
              </Typography>
              <Typography variant="body2" sx={{ color: brandColors.slate }}>
                Status · {viewing.invoiceStatus || invoiceStripeStatus(viewing.status, viewing.kind)}
              </Typography>
              <Typography variant="body2" sx={{ color: brandColors.slate }}>
                Amount · {formatInvoiceAmount(viewing.amountUsd, viewing.currency)}
              </Typography>
              <Typography variant="body2" sx={{ color: brandColors.slate }}>
                Period · {formatUtcDate(viewing.startedAt)} – {formatUtcDate(viewing.expiresAt)}
              </Typography>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setViewing(null)} sx={{ textTransform: 'none', fontWeight: 700, color: brandColors.slate }}>
            Close
          </Button>
          {viewing && (
            <Button
              variant="contained"
              onClick={() => downloadHtml(`${viewing.invoiceNumber}.html`, invoiceHtml(viewing))}
              sx={{
                textTransform: 'none',
                fontWeight: 800,
                bgcolor: brandColors.navy,
                '&:hover': { bgcolor: '#0a0f28' },
              }}
            >
              Download
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={cancelOpen} onClose={() => setCancelOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontFamily: '"Sora", Arial, sans-serif', fontWeight: 800, color: brandColors.navy }}>
          Cancel subscription
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: brandColors.slate }}>
            Cancel this yearly plan? Status becomes Cancelled
            {subscription?.expiresAt ? ` · access continues until ${cycleEnd}` : ''}.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCancelOpen(false)} sx={{ textTransform: 'none', fontWeight: 700, color: brandColors.slate }}>
            Keep plan
          </Button>
          <Button
            variant="contained"
            disabled={cancelling}
            onClick={handleCancel}
            sx={{
              textTransform: 'none',
              fontWeight: 800,
              bgcolor: brandColors.navy,
              '&:hover': { bgcolor: '#0a0f28' },
            }}
          >
            {cancelling ? 'Cancelling…' : 'Cancel subscription'}
          </Button>
        </DialogActions>
      </Dialog>

      <ChangePlanDialog
        open={changeOpen}
        onClose={() => setChangeOpen(false)}
        subscription={subscription}
        catalog={catalog}
        offer={playerOffer}
        onChanged={async (next) => {
          applySubscription(next);
          await reloadInvoices();
        }}
      />
      <PrebookDialog
        open={prebookOpen}
        onClose={() => setPrebookOpen(false)}
        catalog={catalog}
        offer={playerOffer}
        initialConfig={subscription?.planConfig}
        defaultContact={user?.email}
        onPrebooked={async (next) => {
          applySubscription(next);
          await reloadInvoices();
        }}
      />
      <RaisePaymentIssueDialog open={issueOpen} onClose={() => setIssueOpen(false)} />
    </Stack>
  );
};

export default BillingInvoicesPanel;
